import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { ROLE, ROLE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';
import { become } from 'Src/helpers/auth/auth';
import { RoleSession } from 'Src/models/sessions';
import { Role } from 'Src/models/roles';
import './RoleSelector.css';
import { Loader } from 'Src/components/Loader/Loader';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { Permission } from 'Src/models/permission';
import { getFollowerEntryPath } from 'Src/helpers/follower/follower';
import { getRoles } from '../../../helpers/service/admin/user';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronRight,
  faClock,
  faSearch,
  faSignOutAlt,
  faTrophy,
  faUser,
  faUsers
} from '@fortawesome/free-solid-svg-icons';

interface Props {
  history: any;
  user: AppUser;
  setDefaultState: () => void;
  onRoleSelect: (
    roleName: string,
    key: string,
    permissionSchoolId: string,
    permissionSchoolName: string,
    roleSession: RoleSession,
    permission: Permission
  ) => void;
  updateIsRolesNotExist: (isRoleExist: boolean) => void;
}

interface State {
  availableRoles: Role[];
  isLoading: boolean;
  isModalOpen: boolean;
  search: string;
  groupBy: 'school' | 'role';
  choosingPermissionId?: string;
}

const ROLE_META = {
  [ROLE.SLT]: { label: 'School leadership', color: '#164170', tint: '#e9f1fa' },
  [ROLE.ADMIN]: { label: 'Admin', color: '#227ebd', tint: '#e9f1fa' },
  [ROLE.MANAGER]: { label: 'Manager', color: '#7b5bd6', tint: '#efeafb' },
  [ROLE.TEACHER]: { label: 'Teacher', color: '#2f9e5a', tint: '#e7f3ec' },
  [ROLE.COACH]: { label: 'Coach', color: '#f08c1a', tint: '#fdf0dd' },
  [ROLE.PARENT]: { label: 'Parent', color: '#0f8a8a', tint: '#e3f2f2' },
  [ROLE.STUDENT]: { label: 'Student', color: '#d64550', tint: '#fbe9ea' },
  [ROLE.TOURNAMENT_FOLLOWER]: { label: 'Tournament follower', color: '#227ebd', tint: '#eef6fc' }
};

const ROLE_ORDER = [ROLE.SLT, ROLE.ADMIN, ROLE.MANAGER, ROLE.TEACHER, ROLE.COACH, ROLE.PARENT, ROLE.STUDENT, ROLE.TOURNAMENT_FOLLOWER];
const CREST_COLORS = [
  '#12386b',
  '#227ebd',
  '#2f9e5a',
  '#0f7a5a',
  '#0f8a8a',
  '#7b5bd6',
  '#c94fa0',
  '#a01d2e',
  '#c85a1e',
  '#5b6b7b'
];

type LoginPermission = Permission & { role: string };

export class RoleSelector extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      availableRoles: [],
      isLoading: false,
      isModalOpen: false,
      search: '',
      groupBy: 'school',
      choosingPermissionId: undefined
    };
  }

  componentDidMount() {
    const user = this.props.user;

    this.setState({
      isLoading: true
    });

    getRoles(user)
      .then((roles: Role[]) => {
        const rolesCount = roles.length;
        switch (rolesCount) {
          case 0:
            const { updateIsRolesNotExist } = this.props;
            updateIsRolesNotExist(true);
            break;
          case 1:
            const [role] = roles;
            if (role.permissions.length === 1) {
              this.becomeByRole(role);
            } else {
              this.setState({
                availableRoles: roles,
                isLoading: false
              });
            }
            break;
          default:
            this.setState({
              availableRoles: roles,
              isLoading: false
            });
        }
      })
      .catch(err => {
        this.props.setDefaultState();
      });
  }

  becomeByRole(role) {
    const user = this.props.user;
    const roleName = role.name;
    const [permission] = role.permissions;
    const permissionSchoolId = role.permissions[0].schoolId;
    const permissionSchoolName = role.permissions[0].school ? role.permissions[0].school.name : '';

    if (roleName === ROLE.TOURNAMENT_FOLLOWER) {
      this.props.history.push(getFollowerEntryPath(permissionSchoolId));
      return;
    }

    const isRoleAdmin = roleName === ROLE.ADMIN;
    const isRoleManager = roleName === ROLE.MANAGER;
    const isRoleParent = roleName === ROLE.PARENT;
    const isRoleCoach = roleName === ROLE.COACH;
    const isRoleTeacher = roleName === ROLE.TEACHER;
    const isRoleStudent = roleName === ROLE.STUDENT;

    const isAccessEnabled =
      roleName === ROLE.SLT || isRoleAdmin || isRoleManager || isRoleParent || isRoleCoach || isRoleTeacher || isRoleStudent;

    if (!isAccessEnabled) {
      this.setState({
        isModalOpen: true,
        isLoading: false
      });
    } else {
      become(user, roleName).then((roleSession: RoleSession) => {
        const { role: userRole, key } = roleSession;

        this.props.onRoleSelect(userRole, key, permissionSchoolId, permissionSchoolName, roleSession, permission);
      });
    }
  }

  becomeByPermission(permission: LoginPermission) {
    const { user, onRoleSelect } = this.props;
    const { schoolId, preset, school } = permission;

    const permissionSchoolId = schoolId;
    const permissionSchoolName = school ? school.name : '';

    if (preset === ROLE.TOURNAMENT_FOLLOWER) {
      this.props.history.push(getFollowerEntryPath(permissionSchoolId));
      return;
    }

    const isRoleAdmin = preset === ROLE.ADMIN;
    const isRoleManager = preset === ROLE.MANAGER;
    const isRoleParent = preset === ROLE.PARENT;
    const isRoleCoach = preset === ROLE.COACH;
    const isRoleTeacher = preset === ROLE.TEACHER;
    const isRoleStudent = preset === ROLE.STUDENT;

    const isAccessEnabled =
      preset === ROLE.SLT || isRoleAdmin || isRoleManager || isRoleParent || isRoleCoach || isRoleTeacher || isRoleStudent;

    if (!isAccessEnabled) {
      this.setState({
        isModalOpen: true,
        isLoading: false
      });
    } else {
      const permissionKey = this.getPermissionKey(permission);
      this.setState({ choosingPermissionId: permissionKey });
      become(user, preset).then((roleSession: RoleSession) => {
        const { role: userRole, key } = roleSession;

        onRoleSelect(userRole, key, permissionSchoolId, permissionSchoolName, roleSession, permission);
      });
    }
  }

  //copy-paste from old frontend
  getPermissions(roles: Role[]): LoginPermission[] {
    if (roles && roles.length > 0) {
      let permissions: LoginPermission[] = [];

      let isAlreadyHaveParentPermission = false;
      let isAlreadyHaveStudentPermission = false;

      roles.forEach(role => {
        role.permissions.forEach(permission => {
          // Always add all permissions besides PARENT and STUDENT permissions.
          // Add parent and student permissions only at once.
          switch (true) {
            case permission.preset === 'PARENT' && !isAlreadyHaveParentPermission:
              permissions.push({
                ...permission,
                role: role.name
              });
              isAlreadyHaveParentPermission = true;
              break;
            case permission.preset === 'STUDENT' && !isAlreadyHaveStudentPermission:
              permissions.push({
                ...permission,
                role: role.name
              });
              isAlreadyHaveStudentPermission = true;
              break;
            case permission.preset === 'PARENT' && isAlreadyHaveParentPermission:
              break;
            case permission.preset === 'STUDENT' && isAlreadyHaveStudentPermission:
              break;
            default:
              permissions.push({
                ...permission,
                role: role.name
              });
          }
        });
      });

      return permissions;
    } else {
      return [];
    }
  }

  getRoleLabel(role: string) {
    const meta = ROLE_META[role];
    return meta ? meta.label : ROLE_SERVER_TO_CLIENT_MAPPING[role] || role;
  }

  getRoleMeta(role: string) {
    return ROLE_META[role] || { label: this.getRoleLabel(role), color: '#64748b', tint: '#f1f5f9' };
  }

  getPermissionKey(permission: LoginPermission) {
    return permission.id || `${permission.preset}_${permission.schoolId || 'personal'}`;
  }

  getSchoolName(permission: LoginPermission) {
    if (permission.preset === ROLE.PARENT) return '';
    return permission.school ? permission.school.name : '';
  }

  getPrimaryLabel(permission: LoginPermission) {
    const schoolName = this.getSchoolName(permission);
    return schoolName || this.getRoleLabel(permission.preset);
  }

  isUnionPermission(permission: LoginPermission) {
    const schoolKind = permission.school ? (permission.school as any).kind : undefined;
    const schoolName = this.getSchoolName(permission).toLowerCase();
    return (
      schoolKind === 'SchoolUnion' || schoolName.indexOf('ssp') !== -1 || schoolName.indexOf('sport partnership') !== -1
    );
  }

  crestColor(name: string) {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return CREST_COLORS[hash % CREST_COLORS.length];
  }

  initials(name: string) {
    const words = name
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .trim()
      .split(/\s+/);
    const first = words[0] || '';
    const second = words[1] || '';
    return `${first.charAt(0)}${second.charAt(0) || first.charAt(1) || ''}`.toUpperCase();
  }

  filterPermissions(permissions: LoginPermission[]) {
    const q = this.state.search.trim().toLowerCase();
    if (!q) return permissions;

    return permissions.filter(permission => {
      const schoolName = this.getSchoolName(permission).toLowerCase();
      const roleLabel = this.getRoleLabel(permission.preset).toLowerCase();
      return schoolName.indexOf(q) !== -1 || roleLabel.indexOf(q) !== -1;
    });
  }

  getGroupedPermissions(permissions: LoginPermission[]) {
    const filtered = this.filterPermissions(permissions);

    if (this.state.groupBy === 'role') {
      return ROLE_ORDER.map(role => {
        const rows = filtered
          .filter(permission => permission.preset === role)
          .sort((a, b) => this.getPrimaryLabel(a).localeCompare(this.getPrimaryLabel(b)));
        return {
          key: role,
          title: `${this.getRoleLabel(role)}s`,
          rows
        };
      }).filter(group => group.rows.length > 0);
    }

    return [
      {
        key: 'all',
        title: undefined,
        rows: filtered.sort((a, b) => this.getPrimaryLabel(a).localeCompare(this.getPrimaryLabel(b)))
      }
    ];
  }

  renderCrest(permission: LoginPermission) {
    const schoolName = this.getSchoolName(permission);
    const meta = this.getRoleMeta(permission.preset);

    if (permission.preset === ROLE.PARENT) {
      return (
        <span className="rsRoleCrest" style={{ background: meta.tint, color: meta.color }}>
          <FontAwesomeIcon icon={faUsers} />
        </span>
      );
    }

    if (this.isUnionPermission(permission)) {
      return (
        <span className="rsRoleCrest rsRoleCrestUnion">
          <FontAwesomeIcon icon={faTrophy} />
        </span>
      );
    }

    if (!schoolName) {
      return (
        <span className="rsRoleCrest" style={{ background: meta.tint, color: meta.color }}>
          <FontAwesomeIcon icon={faUser} />
        </span>
      );
    }

    const color = this.crestColor(schoolName);
    return (
      <span className="rsRoleCrest" style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
        {this.initials(schoolName)}
      </span>
    );
  }

  renderRoleBadge(role: string) {
    const meta = this.getRoleMeta(role);
    return (
      <span className="rsRoleBadge" style={{ background: meta.tint, color: meta.color }}>
        <span style={{ background: meta.color }} />
        {meta.label}
      </span>
    );
  }

  renderRoleRow(permission: LoginPermission, isRecent = false) {
    const permissionKey = this.getPermissionKey(permission);
    const schoolName = this.getSchoolName(permission);
    const isParent = permission.preset === ROLE.PARENT;
    const primary = isParent ? 'Parent account' : schoolName || this.getRoleLabel(permission.preset);
    const sub = permission.preset === ROLE.TOURNAMENT_FOLLOWER
      ? 'Public tournament access · app and web'
      : isParent
      ? 'Children from linked schools'
      : this.isUnionPermission(permission)
      ? 'School Union Account'
      : schoolName
      ? undefined
      : 'Personal account';
    const isChoosing = this.state.choosingPermissionId === permissionKey;

    return (
      <button
        type="button"
        className={`rsRoleRow ${isRecent ? 'isRecent' : ''} ${isChoosing ? 'isChoosing' : ''}`}
        key={permissionKey}
        onClick={() => this.becomeByPermission(permission)}
      >
        {this.renderCrest(permission)}
        <span className="rsRoleRowText">
          <span className="rsRoleRowPrimary">{primary}</span>
          {sub && <span className="rsRoleRowSub">{sub}</span>}
        </span>
        {schoolName && this.renderRoleBadge(permission.preset)}
        <span className="rsRoleRowIcon">
          <FontAwesomeIcon icon={isChoosing ? faCheck : faChevronRight} />
        </span>
      </button>
    );
  }

  renderRoleList(permissions: LoginPermission[]): React.ReactFragment {
    const filtered = this.filterPermissions(permissions);
    const groups = this.getGroupedPermissions(permissions);
    const recent = permissions[0];
    const showRecent = !!recent && !this.state.search && this.state.groupBy === 'school';

    if (filtered.length === 0) {
      return (
        <div className="rsEmptyState">
          <FontAwesomeIcon icon={faSearch} />
          <strong>No roles match</strong>
          <span>Nothing for “{this.state.search}”.</span>
        </div>
      );
    }

    return (
      <div className="rsRoleListScroll">
        {showRecent && (
          <div className="rsRecentBlock">
            <div className="rsSectionLabel">
              <FontAwesomeIcon icon={faClock} /> Recent
            </div>
            {this.renderRoleRow(recent, true)}
          </div>
        )}

        {groups.map(group => {
          const rows =
            showRecent && group.key === 'all'
              ? group.rows.filter(permission => this.getPermissionKey(permission) !== this.getPermissionKey(recent))
              : group.rows;
          if (rows.length === 0) return null;

          return (
            <div className="rsRoleGroup" key={group.key}>
              {group.title && (
                <div className="rsSectionLabel">
                  {group.title} · {rows.length}
                </div>
              )}
              <div className="rsRoleRows">{rows.map(permission => this.renderRoleRow(permission))}</div>
            </div>
          );
        })}
      </div>
    );
  }

  onSimpleModalButtonClick = () => {
    this.props.setDefaultState();
  };

  render() {
    const { isLoading, isModalOpen, availableRoles, search, groupBy } = this.state;
    const { user } = this.props;

    if (isLoading) {
      return <Loader />;
    }

    if (isModalOpen) {
      return (
        <SimpleModal
          isOpen={isModalOpen}
          buttonText={'Ok'}
          body={
            'You are not authorised to access the school administration console. Ask your school administrator for details.'
          }
          title={'Access forbidden'}
          customClass="rsAccessModal"
          isBodyBold={false}
          onButtonClick={this.onSimpleModalButtonClick}
        />
      );
    }

    const permissions = this.getPermissions(availableRoles);
    const profile: any = user.profile || {};
    const firstName = profile.firstName || profile.name || 'there';
    const fullName =
      [profile.firstName || profile.name, profile.lastName || profile.surname].filter(Boolean).join(' ') ||
      'Signed in user';
    const email = profile.email || '';

    return (
      <div className="rsRolePage">
        <div className="rsRoleCard">
          <header className="rsRoleHeader">
            <div className="rsRoleLogo">
              <img src="/dist/images/logoImage.png" alt="Squad In Touch" />
            </div>
            <h1>Welcome back, {firstName}</h1>
            <p>Choose how you’d like to sign in.</p>
          </header>

          <div className="rsRoleControls">
            <label className="rsRoleSearch">
              <input
                value={search}
                placeholder="Search school or role…"
                onChange={event => this.setState({ search: event.target.value })}
              />
            </label>

            <div className="rsSegment" role="tablist" aria-label="Group roles">
              <button
                type="button"
                className={groupBy === 'school' ? 'isActive' : ''}
                onClick={() => this.setState({ groupBy: 'school' })}
              >
                By school
              </button>
              <button
                type="button"
                className={groupBy === 'role' ? 'isActive' : ''}
                onClick={() => this.setState({ groupBy: 'role' })}
              >
                By role
              </button>
            </div>
          </div>

          {this.renderRoleList(permissions)}

          <footer className="rsRoleFooter">
            <span className="rsUserAvatar">{this.initials(fullName)}</span>
            <span className="rsUserMeta">
              <strong>{fullName}</strong>
              {email && <small>{email}</small>}
            </span>
            <button type="button" className="rsLogoutBtn" onClick={this.props.setDefaultState}>
              <FontAwesomeIcon icon={faSignOutAlt} />
              Log out
            </button>
          </footer>
        </div>
      </div>
    );
  }
}
